import { useMutation, useSuspenseQuery } from '@apollo/client';
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import {
  Content,
  Divider,
  HStack,
  Header,
  PageTitle,
  SidebarTrigger,
  VStack,
  useToast,
} from '@eluve/components';
import { LlmOutputTypesLookup } from '@eluve/graphql-types';
import {
  DynamicArtifactTemplate,
  dynamicArtifactTemplateSchema,
} from '@eluve/llm-outputs';
import {
  DynamicArtifactTemplateBuilder,
  templateBuilderStore,
} from '@eluve/smart-blocks';

import { DynamicArtifactTemplatePreview } from './components/DynamicArtifactPreview';
import { PrefillTemplateSection } from './components/PrefillTemplateSection';
import { TemplateEditingPageHeaderButtons } from './components/TemplateEditingPageHeaderButtons';
import {
  createOutputTemplateVariantMutation,
  getBaseOutputTemplateQuery,
  getOutputTemplateVariantQuery,
  updateTemplateVariantMutation,
} from './operations';
import { useIsOrganizationRoute } from './useIsOrganizationRoute';
import { useIsTemplateDraft } from './useIsTemplateDraft';

export const CreateOutputTemplateVariantPage: React.FC = () => {
  const toast = useToast();
  const location = useLocation();
  const isEditDraftRoute = location.pathname.includes('/edit/');

  const { outputTemplateId, variantNumber } = useParams() as {
    outputTemplateId: string;
    variantNumber: string;
  };
  const isOrganizationRoute = useIsOrganizationRoute();

  useSuspenseQuery(getBaseOutputTemplateQuery, {
    variables: {
      id: outputTemplateId,
    },
  });

  const navigate = useNavigate();
  const navigateTo = `../${outputTemplateId}/variant/${variantNumber}`;

  const { isDraft } = useIsTemplateDraft(outputTemplateId);

  const [isValid, setIsValid] = useState(false);
  const [createOutputTemplateVariant] = useMutation(
    createOutputTemplateVariantMutation,
    {
      onCompleted: ({ insertLlmOutputTemplateVariantsOne: template }) => {
        templateBuilderStore.send({ type: 'reset' });
        if (template) {
          navigate(navigateTo);
        }
      },
      onError: () => toast.error('Failed to create output template'),
    },
  );

  const [updateTemplateVariant] = useMutation(updateTemplateVariantMutation, {
    onCompleted: () => {
      templateBuilderStore.send({ type: 'reset' });
      if (outputTemplateId) {
        navigate(navigateTo);
      }
    },
  });

  const handleSubmit = () => {
    const { context } = templateBuilderStore.getSnapshot();
    const dynamicArtifact: DynamicArtifactTemplate = {
      name: context.name,
      description: context.description,
      blocks: context.blocks,
    };

    // if we're in the edit route, set is_draft to false for the existing draft variant
    if (isEditDraftRoute) {
      updateTemplateVariant({
        variables: {
          outputTemplateId,
          variantNumber: Number(variantNumber),
          isDraft: false,
          template: dynamicArtifact,
          isCurrentDefault: isDraft,
        },
      });
    }

    // If we're in the "clone" path, create a new non-draft variant for this template
    if (!isEditDraftRoute) {
      createOutputTemplateVariant({
        variables: {
          outputTemplateId,
          template: dynamicArtifact,
          variantNotes: context.variantNotes,
          isDraft: false,
        },
      });
    }
  };

  const handleSaveDraft = () => {
    const { context } = templateBuilderStore.getSnapshot();
    const dynamicArtifact: DynamicArtifactTemplate = {
      name: context.name,
      description: context.description,
      blocks: context.blocks,
    };

    // if we're in the edit route, update the existing draft variant
    if (isEditDraftRoute) {
      updateTemplateVariant({
        variables: {
          outputTemplateId,
          variantNumber: Number(variantNumber),
          isDraft: true,
          template: dynamicArtifact,
          isCurrentDefault: false,
        },
      });
    }

    // if we're in the clone route, create a new draft variant for this template
    if (!isEditDraftRoute) {
      createOutputTemplateVariant({
        variables: {
          outputTemplateId,
          template: dynamicArtifact,
          variantNotes: context.variantNotes,
          isDraft: true,
        },
      });
    }
  };

  const {
    data: { llmOutputTemplatesByPk: baseTemplateData },
  } = useSuspenseQuery(getOutputTemplateVariantQuery, {
    variables: {
      id: outputTemplateId,
      variantNumber: Number(variantNumber),
    },
  });

  const baseTemplate = useMemo(() => {
    const parsed = dynamicArtifactTemplateSchema.safeParse(
      baseTemplateData?.variants?.[0]?.template,
    );
    if (!parsed.success) {
      return undefined;
    }

    return parsed.data;
  }, [baseTemplateData]);

  useEffect(() => {
    if (baseTemplate && baseTemplateData) {
      templateBuilderStore.send({
        type: 'hydrateArtifact',
        artifactTemplate: baseTemplate,
        outputType:
          baseTemplateData?.llmOutputType ?? LlmOutputTypesLookup.SOAP_NOTE,
      });
      // Set the parent template's name and description for variants
      templateBuilderStore.send({
        type: 'setName',
        name: baseTemplateData.name ?? '',
      });
      templateBuilderStore.send({
        type: 'setDescription',
        description: baseTemplateData.description ?? '',
      });
    }
  }, [baseTemplate, baseTemplateData]);

  return (
    <>
      <Header>
        {isOrganizationRoute && <SidebarTrigger />}
        <PageTitle isFullWidth>Create Template Variant</PageTitle>

        <TemplateEditingPageHeaderButtons
          handleSaveDraft={handleSaveDraft}
          handleSubmit={handleSubmit}
          handleDeleteDraft={() => navigate(navigateTo)}
          saveDraftDisabled={!isValid}
          submitDisabled={!isValid}
        />
      </Header>
      <Content>
        <HStack align="stretch" className="h-full divide-x">
          <VStack className="h-full min-w-0 flex-1 overflow-y-auto p-6" gap={6}>
            <PrefillTemplateSection />
            <Divider className="my-0" />
            <DynamicArtifactTemplateBuilder
              onIsValidChange={setIsValid}
              showJsonSchema
              hideTemplateDetails={true}
            />
          </VStack>
          <VStack className="h-full min-w-0 flex-1 overflow-y-auto bg-bg-tertiary p-6">
            <DynamicArtifactTemplatePreview />
          </VStack>
        </HStack>
      </Content>
    </>
  );
};
